/**
 * Drawer/modal de edição do produto — TH-53224 (M2.E8.S2.2).
 *
 * Decisão: implementado como Dialog (modal centralizado) e não drawer
 * lateral, pra reusar `Dialog` que já existe no DS sem inventar
 * componente novo. Visualmente cumpre o papel de "edit form" do DoD.
 *
 * PATCH /api/products/:id — só envia campos que mudaram (`exclude_unset`
 * espelhado no cliente). area_id pode ser null para "limpar".
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

interface Product {
  id: string;
  sku: string | null;
  ean: string | null;
  name: string;
  description: string | null;
  unit: string | null;
  image_url: string | null;
  ai_hint: string | null;
  area_id: string | null;
  active: boolean;
}

interface Area {
  id: string;
  name: string;
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  product: Product;
  areas: Area[];
}

interface PatchPayload {
  sku?: string | null;
  ean?: string | null;
  name?: string;
  description?: string | null;
  unit?: string | null;
  image_url?: string | null;
  ai_hint?: string | null;
  area_id?: string | null;
  active?: boolean;
}

async function patchProduct(id: string, payload: PatchPayload): Promise<void> {
  const res = await fetch(`/api/products/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

function nullableString(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

export function ProductEditDrawer({
  open,
  onOpenChange,
  product,
  areas,
}: Props) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(product.name);
  const [sku, setSku] = useState(product.sku ?? "");
  const [ean, setEan] = useState(product.ean ?? "");
  const [unit, setUnit] = useState(product.unit ?? "");
  const [description, setDescription] = useState(product.description ?? "");
  const [imageUrl, setImageUrl] = useState(product.image_url ?? "");
  const [aiHint, setAiHint] = useState(product.ai_hint ?? "");
  const [areaId, setAreaId] = useState(product.area_id ?? "");
  const [active, setActive] = useState(product.active);
  const [error, setError] = useState<string | null>(null);

  // Reset form quando reabre.
  useEffect(() => {
    if (open) {
      setName(product.name);
      setSku(product.sku ?? "");
      setEan(product.ean ?? "");
      setUnit(product.unit ?? "");
      setDescription(product.description ?? "");
      setImageUrl(product.image_url ?? "");
      setAiHint(product.ai_hint ?? "");
      setAreaId(product.area_id ?? "");
      setActive(product.active);
      setError(null);
    }
  }, [open, product]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: PatchPayload = {};
      if (name !== product.name) payload.name = name;
      if (nullableString(sku) !== product.sku)
        payload.sku = nullableString(sku);
      if (nullableString(ean) !== product.ean)
        payload.ean = nullableString(ean);
      if (nullableString(unit) !== product.unit)
        payload.unit = nullableString(unit);
      if (nullableString(description) !== product.description)
        payload.description = nullableString(description);
      if (nullableString(imageUrl) !== product.image_url)
        payload.image_url = nullableString(imageUrl);
      if (nullableString(aiHint) !== product.ai_hint)
        payload.ai_hint = nullableString(aiHint);
      const newAreaId = areaId === "" ? null : areaId;
      if (newAreaId !== product.area_id) payload.area_id = newAreaId;
      if (active !== product.active) payload.active = active;

      if (Object.keys(payload).length === 0) {
        return Promise.reject(new Error("nada a atualizar"));
      }
      return patchProduct(product.id, payload);
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["product", product.id] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setError(null);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogTitle>Editar produto</DialogTitle>
        <DialogDescription>
          Atualize os dados do produto. Apenas campos modificados são enviados.
        </DialogDescription>

        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pe-name">Nome</Label>
            <Input
              id="pe-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pe-sku">SKU</Label>
              <Input
                id="pe-sku"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                maxLength={64}
                className="font-mono text-xs"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pe-ean">EAN</Label>
              <Input
                id="pe-ean"
                value={ean}
                onChange={(e) => setEan(e.target.value)}
                maxLength={14}
                className="font-mono text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pe-unit">Unidade</Label>
              <Input
                id="pe-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                maxLength={16}
                placeholder="UN, KG, L…"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pe-area">Área</Label>
              <Select
                id="pe-area"
                value={areaId}
                onChange={(e) => setAreaId(e.target.value)}
              >
                <option value="">— sem área —</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pe-image">URL da imagem</Label>
            <Input
              id="pe-image"
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pe-desc">Descrição</Label>
            <textarea
              id="pe-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pe-aihint">Dica para IA (opcional)</Label>
            <Input
              id="pe-aihint"
              value={aiHint}
              onChange={(e) => setAiHint(e.target.value)}
              placeholder="ex.: foto sobre madeira rústica"
            />
          </div>

          <label className="mt-1 inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 rounded border-[var(--color-border-strong)]"
            />
            <span>Ativo</span>
          </label>

          {error ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {error}
            </div>
          ) : null}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !name.trim()}
            >
              {mutation.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
